<template>
	<div :class="{'logged': loggedIn}" class="sidebar">
		<div class="close" @click="closeMenu()">
			<font-awesome-icon icon="times" />
		</div>

		<div class="sidebar-body">
			<div v-if="loggedIn" @click="emit('onNavigation')">
				<router-link v-slot="{ navigate }" :to="{name: 'profile'}" custom>
					<div class="d-flex flex-vertical-align mt-2" @click="navigate">
						<div class="user-thumbnail">
							<UserThumbnail :image-url="image" />
						</div>
						<div class="user-fullname-title">
							{{ fullName }}
						</div>
					</div>
				</router-link>
			</div>

			<div class="clearfix" />
			<champion-monthly v-if="loggedIn && isInMonthlyTop" :monthly-top-url="monthlyTopUrl" />

			<ul class="menu" @click="emit('onNavigation')">
				<template v-if="loggedIn">
					<router-link v-if="!isInMainMenu('rewards')" v-slot="{ navigate }" :to="{name: 'prizes-rewards'}" custom>
						<li class="list-item" @click="navigate">
							<span style="position:relative">{{ t.mainMenuMyRewards }} <span v-if="numberOfMyRewards > 0 && settings.ui.showUnusedRewardsBadge" class="badge-notification">{{ numberOfMyRewards }}</span></span>
						</li>
					</router-link>

					<router-link v-if="settings.userFriendsEnabled && !isInMainMenu('leaderboard')" v-slot="{ navigate }" :to="{name: 'leaderboard'}" custom>
						<li class="list-item" @click="navigate">
							{{ t.mainMenuLeaderboard }}
						</li>
					</router-link>

					<router-link v-if="!isInMainMenu('profile')" v-slot="{ navigate }" :to="{name: 'profile', params: { language: currentLanguage }}" custom>
						<li class="list-item" @click="navigate">
							{{ t.mainMenuProfile }}
						</li>
					</router-link>

					<router-link v-if="!isInMainMenu('history')" v-slot="{ navigate }" :to="{name: 'history'}" custom>
						<li class="list-item" @click="navigate">
							{{ t.mainMenuHistory }}
						</li>
					</router-link>

					<router-link v-if="!isInMainMenu('help')" v-slot="{ navigate }" :to="{name: 'help', params: { language: currentLanguage }}" custom>
						<li class="list-item" @click="navigate">
							{{ t.mainMenuHelp }}
						</li>
					</router-link>
				</template>

				<router-link v-if="settings.newsEnabled && !isInMainMenu('news')" v-slot="{ navigate }" :to="{name: 'news', params: { language: currentLanguage }}" custom>
					<li class="list-item" @click="navigate">
						<span style="position:relative">{{ t.mainMenuNews }}
							<span v-if="newsState && newsState.unreadCount > 0" class="badge-notification">{{ newsState.unreadCount }}</span></span>
					</li>
				</router-link>

				<!-- Text menu items -->
				<router-link v-for="item in menuItems" v-slot="{ navigate }" :key="item.name" :to="{name: item.name, params: { language: currentLanguage }}" custom>
					<li class="list-item" @click="navigate">
						{{ item.title }}
					</li>
				</router-link>
			</ul>

			<div v-if="loggedIn" class="logout-button" @click.stop.prevent="onLogout()">
				<font-awesome-icon icon="sign-out-alt" class="mr-2" />
				<div class="logout-button-text">
					{{ t.mainLogOutButton }}
				</div>
			</div>
		</div>

		<div class="sidebar-footer">
			<p v-if="languagesSupported.length > 1">
				<font-awesome-icon icon="globe-europe" class="mr-2" />
				
				<!-- Footer menu -->
				<a
					v-for="language in languagesSupported"
					:key="language.key" :href="'/'+language.key + ( loggedIn ? '/dashboard' : '/sign-in' )"
					:class="{'language-active': currentLanguage == language.key}" class="language-switch mr-2"
					@click.stop.prevent="switchLanguage(language.key); closeMenu() "
				>
					{{ language.name }}
				</a>
			</p>

			<div v-if="showSocialIcons" class="social-icons pull-right">
				<div v-if="settings.facebookUrl">
					<a :href="settings.facebookUrl" target="_blank">
						<font-awesome-icon :icon="['fab', 'facebook-f']" />
					</a>
				</div>
				<div v-if="settings.youtubeUrl">
					<a :href="settings.youtubeUrl" target="_blank">
						<font-awesome-icon :icon="['fab', 'youtube']" />
					</a>
				</div>
			</div>

			<p v-if="t.copyright">
				{{ t.copyright }}
			</p>
		</div>
	</div>
</template>

<script setup>
import { computed } from 'vue';
import { includes } from "lodash-es"
import { logout } from '@/js/includes/shared';
import ChampionMonthly from './champion-monthly.vue'
import { switchLanguage } from '@/js/classes/translations'
import { useStore } from '@/js/stores/index';
import { useUserStore } from '@/js/stores/user';
import { useNewsStore } from '@/js/stores/news';
import UserThumbnail from './user-thumbnail.vue';

const store = useStore()
const userStore = useUserStore()
const newsStore = useNewsStore()

// Emits
const emit = defineEmits(["onNavigation", 'onSideMenuClose'])

// Computeds
const t = computed(() => store.translations)
const currentLanguage = computed(() => store.currentLanguage)
const loggedIn = computed(() => userStore.loggedIn)
const fullName = computed(() => userStore.userFullName)
const image = computed(() => userStore.userImage)
const showSocialIcons = computed(() => settings.value?.facebookUrl || settings.value?.youtubeUrl)
const settings = computed(() => store.settings)
const newsState = computed(() => newsStore)
const languagesSupported = computed(() => store.settings.languagesSupported)
const menuItems = computed(() => store.menuItems)
const numberOfMyRewards = computed(() => store.rewards?.length || 0)
const isInMonthlyTop = computed(() => userStore.isInMonthlyTop)
const monthlyTopUrl = computed(() => userStore.monthlyTopUrl)

// Functions
function closeMenu(){
	emit('onSideMenuClose')
}

async function onLogout(){
	await logout()
	closeMenu()
}

function isInMainMenu(item){
	return includes(settings.value.ui.mainMenuOrderMobile, item)
}
</script>
